import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const ICE_CONSENT_TEXT =
  "Autorizo que los organizadores y barredores de esta rodada accedan a mi ficha de emergencia, únicamente durante el evento. Puedo revocar este permiso cuando quiera.";

/**
 * Confirmation step shown before joining an event.
 * The ICE consent checkbox is optional and unchecked by default.
 */
export function JoinConsentDialog({
  eventId,
  eventName,
  userId,
  onClose,
  onJoined,
}: {
  eventId: string | null;
  eventName?: string | null;
  userId: string;
  onClose: () => void;
  onJoined: (eventId: string) => void | Promise<void>;
}) {
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasCard, setHasCard] = useState<boolean | null>(null);

  useEffect(() => {
    if (!eventId) return;
    setConsent(false);
    let cancelled = false;
    (async () => {
      setHasCard(null);
      const [{ data: prof }, { count }] = await Promise.all([
        supabase.from("emergency_profiles").select("id").eq("user_id", userId).maybeSingle(),
        supabase
          .from("emergency_contacts")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId),
      ]);
      if (cancelled) return;
      setHasCard(!!prof && (count ?? 0) > 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, userId]);

  if (!eventId) return null;

  const confirm = async () => {
    setSubmitting(true);
    const { error } = await supabase
      .from("inscripciones")
      .insert({ evento_id: eventId, usuario_id: userId });
    if (error) {
      setSubmitting(false);
      toast.error(error.message);
      return;
    }
    if (consent) {
      const { error: cErr } = await supabase
        .from("event_ice_consents")
        .upsert(
          { event_id: eventId, user_id: userId, revoked_at: null },
          { onConflict: "event_id,user_id" },
        );
      if (cErr) toast.error("Te inscribiste, pero no se pudo guardar el consentimiento.");
    }
    setSubmitting(false);
    toast.success("¡Inscrito!");
    await onJoined(eventId);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-background/80 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg bg-card border border-border rounded-lg p-6 space-y-5 shadow-xl">
        <div>
          <h2 className="font-display text-xl">Confirmar inscripción</h2>
          {eventName ? <p className="text-sm text-muted-foreground mt-1">{eventName}</p> : null}
        </div>

        <label className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="w-4 h-4 mt-0.5 accent-[hsl(var(--primary))]"
          />
          <span className="text-sm leading-relaxed">
            <ShieldCheck className="w-4 h-4 inline-block mr-1.5 text-primary align-[-2px]" />
            {ICE_CONSENT_TEXT}
          </span>
        </label>

        {hasCard === false ? (
          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/20 p-4 text-sm">
            <AlertTriangle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>
              Aún no tienes una ficha de emergencia. Créala para que tu club pueda ayudarte si algo
              pasa en la rodada.{" "}
              <Link
                to="/perfil"
                search={{ tab: "emergencia" }}
                className="text-primary hover:underline font-semibold"
              >
                Crear mi ficha
              </Link>
            </span>
          </div>
        ) : null}

        <p className="text-xs text-muted-foreground">
          El consentimiento es opcional: puedes inscribirte sin autorizarlo.
        </p>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2.5 rounded-md border border-border text-sm font-semibold hover:bg-muted/50 disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={submitting}
            className="px-5 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Confirmar inscripción
          </button>
        </div>
      </div>
    </div>
  );
}
